import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    ResponsiveContainer, ComposedChart, BarChart, Bar, Line,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { FormControl, Select, MenuItem, Button, CircularProgress } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ProductSelector from './ProductSelector';
import supabase from './supabaseClient';

/**
 * PostVolumePanel — live view of `topic_volume_stats` via the
 * `get_topic_volume_series` RPC.
 *
 * Why this exists separately from PostAnalytics: PostAnalytics charts posts we
 * actually STORED. Comment ingestion for big-community scrapes is gated (top-N
 * per post + brand mentions), so the size of the raw stream can't be derived
 * from the posts table — scanners measure it during the walk and persist it to
 * topic_volume_stats. This panel reads that measurement directly, so the
 * numbers that used to live in a static PDF render live.
 *
 * RPC contract (hnscraper/migrations/get_topic_volume_series.sql):
 *   get_topic_volume_series(topic_id_param UUID, scope_param TEXT DEFAULT NULL,
 *     granularity_param TEXT DEFAULT 'day', start_time_param BIGINT DEFAULT NULL,
 *     end_time_param BIGINT DEFAULT NULL)
 *   -> TABLE (scope TEXT, period_start TIMESTAMPTZ, period_end TIMESTAMPTZ,
 *             volume_count BIGINT)
 * Time params are epoch MILLISECONDS.
 *
 * Scope convention: '<community>/comments' is the raw stream that was scanned;
 * '<community>/comment_mentions' is how many of those actually matched the
 * topic and were kept. Daily rows exist for the raw stream; the mention counts
 * are recorded once per scrape window, which is why the capture-rate view is
 * built from 'window' granularity and the time series from 'day'.
 */

// Categorical slots, fixed order (validated: adjacent CVD dE 9.1, normal-vision
// 19.6 on a light surface). Never cycled — a 9th scope folds into "Other".
const SERIES_COLORS = [
    '#2a78d6', '#eb6834', '#1baf7a', '#eda100',
    '#e87ba4', '#008300', '#4a3aa7', '#e34948'
];
const OTHER_COLOR = '#8a8a85';
const MAX_SERIES = 8;

const TEXT_PRIMARY = '#111';
const TEXT_SECONDARY = '#52514e';
const TEXT_MUTED = '#999';
const GRID_COLOR = '#e8e8e4';

const BUCKET_OPTIONS = [
    { value: 'day', label: 'Daily' },
    { value: 'week', label: 'Weekly' },
    { value: 'month', label: 'Monthly' }
];

const ROLLING_OPTIONS = [
    { value: 0, label: 'Off' },
    { value: 7, label: '7 periods' },
    { value: 14, label: '14 periods' },
    { value: 30, label: '30 periods' }
];

// ---------------------------------------------------------------- scope utils

// Scopes look like '<community>/<kind>'. Community names can't contain '/', but
// split on the LAST separator anyway so an unexpected shape degrades sensibly.
const splitScope = (scope) => {
    const s = String(scope || '');
    const i = s.lastIndexOf('/');
    if (i === -1) return { community: s, kind: '' };
    return { community: s.slice(0, i), kind: s.slice(i + 1) };
};

const KIND_LABELS = {
    comments: 'raw comment stream',
    comment_mentions: 'matched mentions',
    posts: 'posts'
};

const scopeLabel = (scope) => {
    const { community, kind } = splitScope(scope);
    if (!kind) return community;
    return `${community} · ${KIND_LABELS[kind] || kind}`;
};

const isRawCommentScope = (scope) => splitScope(scope).kind === 'comments';
const isMentionScope = (scope) => splitScope(scope).kind === 'comment_mentions';

// -------------------------------------------------------------- date helpers

// period_start comes back as a timestamptz. Normalise through UTC so a viewer
// west of Greenwich doesn't see every bucket shift back a day.
const toDayKey = (iso) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
};

// Re-hydrate a 'YYYY-MM-DD' key as a LOCAL midnight so date-fns formats the day
// we actually mean (parseISO on a bare date is local; on a Z-suffixed one it
// would shift again).
const fromDayKey = (key) => parseISO(`${key}T00:00:00`);

const bucketKeyFor = (dayKey, bucket) => {
    if (bucket === 'day') return dayKey;
    const d = fromDayKey(dayKey);
    if (bucket === 'week') {
        const start = new Date(d);
        start.setDate(start.getDate() - start.getDay()); // week starts Sunday
        return format(start, 'yyyy-MM-dd');
    }
    return format(new Date(d.getFullYear(), d.getMonth(), 1), 'yyyy-MM-dd');
};

const bucketTickLabel = (dayKey, bucket) => {
    const d = fromDayKey(dayKey);
    if (bucket === 'month') return format(d, 'MMM yyyy');
    return format(d, 'MMM d');
};

const fmtInt = (n) => Number(n || 0).toLocaleString();
const fmtPct = (n) => (n == null ? '—' : `${n.toFixed(1)}%`);

// ------------------------------------------------------------------ presentation

const CONTROL_HEIGHT = 36;

const selectStyle = {
    width: '100%',
    height: CONTROL_HEIGHT,
    '& .MuiSelect-select': {
        paddingTop: '6px',
        paddingBottom: '6px',
        paddingRight: '24px !important'
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d0d0d0' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#999' }
};

const filterLabelStyle = {
    fontSize: '9px',
    fontWeight: 600,
    color: TEXT_MUTED,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '3px'
};

const dateInputStyle = {
    padding: '6px 10px',
    borderRadius: '4px',
    border: '1px solid #d0d0d0',
    fontSize: '13px',
    height: CONTROL_HEIGHT,
    boxSizing: 'border-box',
    outline: 'none',
    width: '100%'
};

const StatTile = ({ label, value, hint }) => (
    <div style={{
        flex: '1 1 140px',
        minWidth: 140,
        padding: '12px 14px',
        borderRadius: '8px',
        border: '1px solid rgba(0,0,0,0.07)',
        backgroundColor: '#fff'
    }}>
        <div style={{
            fontSize: '9px', fontWeight: 600, color: TEXT_MUTED,
            textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px'
        }}>
            {label}
        </div>
        <div style={{ fontSize: '20px', fontWeight: 700, color: TEXT_PRIMARY, lineHeight: 1.15 }}>
            {value}
        </div>
        {hint && (
            <div style={{ fontSize: '11px', color: TEXT_MUTED, marginTop: '2px' }}>{hint}</div>
        )}
    </div>
);

const SeriesTooltip = ({ active, payload, label, bucket }) => {
    if (!active || !payload || payload.length === 0) return null;
    const rows = payload.filter(p => p.value != null && p.value !== 0);
    if (rows.length === 0) return null;
    return (
        <div style={{
            backgroundColor: '#fff',
            border: '1px solid rgba(0,0,0,0.1)',
            borderRadius: '6px',
            padding: '8px 10px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            fontSize: '12px'
        }}>
            <div style={{ fontWeight: 600, color: TEXT_PRIMARY, marginBottom: '4px' }}>
                {bucket === 'month' ? label : label}
            </div>
            {rows.map(p => (
                <div key={p.dataKey} style={{ display: 'flex', alignItems: 'center', gap: '6px', color: TEXT_SECONDARY }}>
                    <span style={{
                        width: 8, height: 8, borderRadius: 2,
                        backgroundColor: p.color, display: 'inline-block', flex: '0 0 auto'
                    }} />
                    <span style={{ flex: 1 }}>{p.name}</span>
                    <span style={{ fontWeight: 600, color: TEXT_PRIMARY }}>
                        {typeof p.value === 'number' ? p.value.toLocaleString() : p.value}
                    </span>
                </div>
            ))}
        </div>
    );
};

const EmptyState = ({ title, detail }) => (
    <div style={{
        padding: '48px 24px',
        textAlign: 'center',
        color: TEXT_SECONDARY,
        backgroundColor: '#fafafa',
        borderRadius: '8px',
        border: '1px dashed rgba(0,0,0,0.12)'
    }}>
        <div style={{ fontSize: '15px', fontWeight: 600, color: TEXT_PRIMARY, marginBottom: '6px' }}>
            {title}
        </div>
        <div style={{ fontSize: '13px', maxWidth: 520, margin: '0 auto', lineHeight: 1.5 }}>
            {detail}
        </div>
    </div>
);

const DataTable = ({ columns, rows, caption }) => (
    <div style={{ marginTop: '16px', overflowX: 'auto' }}>
        {caption && (
            <div style={{
                fontSize: '9px', fontWeight: 600, color: TEXT_MUTED,
                textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px'
            }}>
                {caption}
            </div>
        )}
        <table style={{ borderCollapse: 'collapse', fontSize: '12px', minWidth: '100%' }}>
            <thead>
                <tr>
                    {columns.map(c => (
                        <th key={c.key} style={{
                            textAlign: c.align || 'left',
                            padding: '6px 12px 6px 0',
                            borderBottom: '1px solid #e0e0dc',
                            color: TEXT_SECONDARY,
                            fontWeight: 600,
                            whiteSpace: 'nowrap'
                        }}>
                            {c.label}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {rows.map((r, i) => (
                    <tr key={r.key ?? i}>
                        {columns.map(c => (
                            <td key={c.key} style={{
                                textAlign: c.align || 'left',
                                padding: '5px 12px 5px 0',
                                borderBottom: '1px solid #f2f2ef',
                                color: TEXT_PRIMARY,
                                whiteSpace: 'nowrap'
                            }}>
                                {c.render ? c.render(r) : r[c.key]}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

// ================================================================== component

const PostVolumePanel = ({ topicMap, companyName, view = 'daily' }) => {
    const [selectedProduct, setSelectedProduct] = useState('');
    const [dailyRows, setDailyRows] = useState([]);
    const [windowRows, setWindowRows] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasFetched, setHasFetched] = useState(false);

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [scopeFilter, setScopeFilter] = useState('all');
    const [bucket, setBucket] = useState('day');
    const [rollingPeriods, setRollingPeriods] = useState(7);
    const [showTable, setShowTable] = useState(false);
    const [isHeaderHovered, setIsHeaderHovered] = useState(false);

    // Pull the whole series for the topic once, then filter/aggregate in the
    // browser. Volume rows are one per scope per day, so the payload is small
    // and every control below re-renders instantly instead of round-tripping.
    const fetchSeries = useCallback(async (topicId) => {
        if (!topicId) return;
        setIsLoading(true);
        setError(null);
        try {
            const [daily, windows] = await Promise.all([
                supabase.rpc('get_topic_volume_series', {
                    topic_id_param: topicId,
                    granularity_param: 'day'
                }),
                supabase.rpc('get_topic_volume_series', {
                    topic_id_param: topicId,
                    granularity_param: 'window'
                })
            ]);

            if (daily.error) throw daily.error;
            if (windows.error) throw windows.error;

            const normalise = (rows) => (rows || []).map(r => ({
                scope: r.scope,
                periodStart: r.period_start,
                periodEnd: r.period_end,
                dayKey: toDayKey(r.period_start),
                count: Number(r.volume_count ?? 0)
            })).filter(r => r.dayKey);

            setDailyRows(normalise(daily.data));
            setWindowRows(normalise(windows.data));
        } catch (e) {
            console.error('get_topic_volume_series failed:', e);
            setError(e);
            setDailyRows([]);
            setWindowRows([]);
        } finally {
            setIsLoading(false);
            setHasFetched(true);
        }
    }, []);

    useEffect(() => {
        if (!selectedProduct) return;
        setHasFetched(false);
        setScopeFilter('all');
        fetchSeries(selectedProduct);
    }, [selectedProduct, fetchSeries]);

    // Default the date inputs to the full observed range the first time data for
    // a topic lands, so the panel opens showing everything that was measured.
    useEffect(() => {
        if (dailyRows.length === 0) return;
        const keys = dailyRows.map(r => r.dayKey).sort();
        setStartDate(keys[0]);
        setEndDate(keys[keys.length - 1]);
    }, [dailyRows]);

    const topicName = useMemo(() => {
        const t = topicMap?.[selectedProduct];
        return t?.name || t?.topic || 'Product';
    }, [topicMap, selectedProduct]);

    // ------------------------------------------------------------- daily view

    const inRange = useCallback((row) => {
        if (startDate && row.dayKey < startDate) return false;
        if (endDate && row.dayKey > endDate) return false;
        return true;
    }, [startDate, endDate]);

    // Only the raw comment stream has daily rows; keep the view honest about
    // what's actually charted rather than silently mixing granularities.
    const dailyScopes = useMemo(() => {
        const totals = new Map();
        dailyRows.forEach(r => totals.set(r.scope, (totals.get(r.scope) || 0) + r.count));
        return [...totals.entries()]
            .sort((a, b) => b[1] - a[1])
            .map(([scope]) => scope);
    }, [dailyRows]);

    // Fixed-order slot assignment keyed on the scope itself, so narrowing the
    // scope filter never repaints the survivors.
    const scopeColor = useCallback((scope) => {
        const i = dailyScopes.indexOf(scope);
        if (i === -1 || i >= MAX_SERIES) return OTHER_COLOR;
        return SERIES_COLORS[i];
    }, [dailyScopes]);

    const chartedScopes = useMemo(() => {
        if (scopeFilter !== 'all') return dailyScopes.filter(s => s === scopeFilter);
        return dailyScopes.slice(0, MAX_SERIES);
    }, [dailyScopes, scopeFilter]);

    const overflowScopes = useMemo(
        () => (scopeFilter === 'all' ? dailyScopes.slice(MAX_SERIES) : []),
        [dailyScopes, scopeFilter]
    );

    const dailyChart = useMemo(() => {
        const rows = dailyRows.filter(inRange).filter(r =>
            scopeFilter === 'all' ? true : r.scope === scopeFilter
        );
        if (rows.length === 0) return [];

        const charted = new Set(chartedScopes);
        const buckets = new Map();
        rows.forEach(r => {
            const key = bucketKeyFor(r.dayKey, bucket);
            if (!buckets.has(key)) buckets.set(key, { bucketKey: key, total: 0 });
            const b = buckets.get(key);
            const seriesKey = charted.has(r.scope) ? r.scope : '__other__';
            b[seriesKey] = (b[seriesKey] || 0) + r.count;
            b.total += r.count;
        });

        const ordered = [...buckets.values()].sort((a, b) => (a.bucketKey < b.bucketKey ? -1 : 1));
        ordered.forEach(b => { b.label = bucketTickLabel(b.bucketKey, bucket); });

        if (rollingPeriods > 0) {
            ordered.forEach((b, i) => {
                const from = Math.max(0, i - (rollingPeriods - 1));
                const window = ordered.slice(from, i + 1);
                const sum = window.reduce((acc, x) => acc + x.total, 0);
                b.rollingAvg = Math.round((sum / window.length) * 10) / 10;
            });
        }
        return ordered;
    }, [dailyRows, inRange, scopeFilter, chartedScopes, bucket, rollingPeriods]);

    const dailyStats = useMemo(() => {
        const rows = dailyRows.filter(inRange).filter(r =>
            scopeFilter === 'all' ? true : r.scope === scopeFilter
        );
        const total = rows.reduce((a, r) => a + r.count, 0);
        const byDay = new Map();
        rows.forEach(r => byDay.set(r.dayKey, (byDay.get(r.dayKey) || 0) + r.count));
        const days = [...byDay.entries()].sort((a, b) => b[1] - a[1]);
        const peak = days[0];
        return {
            total,
            days: byDay.size,
            perDay: byDay.size ? Math.round(total / byDay.size) : 0,
            peakCount: peak ? peak[1] : 0,
            peakDay: peak ? format(fromDayKey(peak[0]), 'MMM d, yyyy') : '—'
        };
    }, [dailyRows, inRange, scopeFilter]);

    // ---------------------------------------------------------- comments view

    // Raw stream vs what was actually kept, per community. Mention counts land
    // once per scrape window, so this pairs window rows rather than daily ones.
    const commentRows = useMemo(() => {
        const byCommunity = new Map();
        windowRows.forEach(r => {
            const { community } = splitScope(r.scope);
            if (!isRawCommentScope(r.scope) && !isMentionScope(r.scope)) return;
            if (!byCommunity.has(community)) {
                byCommunity.set(community, {
                    key: community, community, scanned: 0, mentions: 0,
                    windowStart: null, windowEnd: null
                });
            }
            const row = byCommunity.get(community);
            if (isRawCommentScope(r.scope)) row.scanned += r.count;
            else row.mentions += r.count;
            if (!row.windowStart || r.periodStart < row.windowStart) row.windowStart = r.periodStart;
            if (!row.windowEnd || r.periodEnd > row.windowEnd) row.windowEnd = r.periodEnd;
        });
        return [...byCommunity.values()]
            .map(r => ({
                ...r,
                captureRate: r.scanned > 0 ? (r.mentions / r.scanned) * 100 : null
            }))
            .sort((a, b) => b.scanned - a.scanned);
    }, [windowRows]);

    const commentTotals = useMemo(() => {
        const scanned = commentRows.reduce((a, r) => a + r.scanned, 0);
        const mentions = commentRows.reduce((a, r) => a + r.mentions, 0);
        return {
            scanned,
            mentions,
            captureRate: scanned > 0 ? (mentions / scanned) * 100 : null,
            communities: commentRows.length
        };
    }, [commentRows]);

    // Daily raw-stream series, charted on its own so the capture-rate percentage
    // never has to share a y-axis with a count.
    const commentDailyChart = useMemo(() => {
        const rows = dailyRows.filter(r => isRawCommentScope(r.scope)).filter(inRange);
        if (rows.length === 0) return [];
        const buckets = new Map();
        rows.forEach(r => {
            const key = bucketKeyFor(r.dayKey, bucket);
            if (!buckets.has(key)) buckets.set(key, { bucketKey: key, total: 0 });
            buckets.get(key).total += r.count;
        });
        const ordered = [...buckets.values()].sort((a, b) => (a.bucketKey < b.bucketKey ? -1 : 1));
        ordered.forEach(b => { b.label = bucketTickLabel(b.bucketKey, bucket); });
        return ordered;
    }, [dailyRows, inRange, bucket]);

    // ----------------------------------------------------------------- render

    const rpcMissing = error && (error.code === 'PGRST202' || /schema cache/i.test(error.message || ''));

    const subline = useMemo(() => {
        const parts = [];
        if (startDate && endDate) {
            parts.push(`${format(fromDayKey(startDate), 'MMM d, yyyy')} – ${format(fromDayKey(endDate), 'MMM d, yyyy')}`);
        }
        parts.push(view === 'comments' ? 'Raw stream vs. matched mentions' : 'Observed stream volume');
        if (view === 'daily') {
            parts.push(BUCKET_OPTIONS.find(b => b.value === bucket)?.label || 'Daily');
        }
        return parts.join(' • ');
    }, [startDate, endDate, view, bucket]);

    const renderControls = () => (
        <div style={{
            display: 'flex', alignItems: 'flex-end', gap: '12px',
            flexWrap: 'wrap', rowGap: '12px'
        }}>
            <div style={{ flex: '1 1 auto', minWidth: 120, maxWidth: 180 }}>
                <div style={filterLabelStyle}>Product</div>
                <ProductSelector
                    topicMap={topicMap}
                    selectedId={selectedProduct || ''}
                    onSelect={setSelectedProduct}
                    label=""
                    size="small"
                    sx={selectStyle}
                />
            </div>

            <div style={{ flex: '0 0 auto', minWidth: 120 }}>
                <div style={filterLabelStyle}>Start Date</div>
                <input
                    type="date"
                    value={startDate}
                    max={endDate || undefined}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={dateInputStyle}
                />
            </div>

            <div style={{ flex: '0 0 auto', minWidth: 120 }}>
                <div style={filterLabelStyle}>End Date</div>
                <input
                    type="date"
                    value={endDate}
                    min={startDate || undefined}
                    onChange={(e) => setEndDate(e.target.value)}
                    style={dateInputStyle}
                />
            </div>

            {view === 'daily' && dailyScopes.length > 1 && (
                <div style={{ flex: '1 1 auto', minWidth: 160, maxWidth: 240 }}>
                    <div style={filterLabelStyle}>Scope</div>
                    <FormControl size="small" sx={selectStyle}>
                        <Select value={scopeFilter} onChange={(e) => setScopeFilter(e.target.value)}>
                            <MenuItem value="all">All scopes</MenuItem>
                            {dailyScopes.map(s => (
                                <MenuItem key={s} value={s}>{scopeLabel(s)}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
            )}

            <div style={{ flex: '1 1 auto', minWidth: 100, maxWidth: 130 }}>
                <div style={filterLabelStyle}>Bucket</div>
                <FormControl size="small" sx={selectStyle}>
                    <Select value={bucket} onChange={(e) => setBucket(e.target.value)}>
                        {BUCKET_OPTIONS.map(o => (
                            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>

            {view === 'daily' && (
                <div style={{ flex: '1 1 auto', minWidth: 110, maxWidth: 140 }}>
                    <div style={filterLabelStyle}>Rolling Avg</div>
                    <FormControl size="small" sx={selectStyle}>
                        <Select
                            value={rollingPeriods}
                            onChange={(e) => setRollingPeriods(Number(e.target.value))}
                        >
                            {ROLLING_OPTIONS.map(o => (
                                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
            )}

            <div style={{ flex: '0 0 auto', alignSelf: 'flex-end' }}>
                <Button
                    variant="outlined"
                    onClick={() => fetchSeries(selectedProduct)}
                    disabled={!selectedProduct || isLoading}
                    startIcon={<RefreshIcon />}
                    sx={{ height: CONTROL_HEIGHT, textTransform: 'none', fontWeight: 600 }}
                >
                    {isLoading ? 'Loading…' : 'Refresh'}
                </Button>
            </div>
        </div>
    );

    const renderBody = () => {
        if (!selectedProduct) {
            return <EmptyState title="Select a product" detail="Choose a product above to load its observed stream volume." />;
        }
        if (isLoading && !hasFetched) {
            return (
                <div style={{ padding: '64px', textAlign: 'center' }}>
                    <CircularProgress size={28} />
                </div>
            );
        }
        if (rpcMissing) {
            return (
                <EmptyState
                    title="Volume series function not available"
                    detail={'The get_topic_volume_series RPC is not deployed to this Supabase project yet. Apply hnscraper/migrations/get_topic_volume_series.sql (and its companion create_topic_volume_stats.sql) and this panel will populate automatically.'}
                />
            );
        }
        if (error) {
            return (
                <EmptyState
                    title="Couldn't load volume series"
                    detail={error.message || 'Unknown error querying get_topic_volume_series.'}
                />
            );
        }
        return view === 'comments' ? renderCommentsView() : renderDailyView();
    };

    const renderDailyView = () => {
        if (dailyRows.length === 0) {
            return (
                <EmptyState
                    title={`No measured volume for ${topicName}`}
                    detail="No daily rows exist in topic_volume_stats for this product yet. Volume is recorded when a scanner walks the community's full stream."
                />
            );
        }

        // The last stacked series carries the rounded cap so the stack reads as
        // one column with a single rounded top.
        const lastScope = chartedScopes[chartedScopes.length - 1];
        const showOther = overflowScopes.length > 0;

        return (
            <>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
                    <StatTile label="Total Volume" value={fmtInt(dailyStats.total)} hint={`${fmtInt(dailyStats.days)} days measured`} />
                    <StatTile label="Average / Day" value={fmtInt(dailyStats.perDay)} />
                    <StatTile label="Peak Day" value={fmtInt(dailyStats.peakCount)} hint={dailyStats.peakDay} />
                    <StatTile label="Scopes" value={fmtInt(dailyScopes.length)} hint={scopeFilter === 'all' ? 'all charted' : scopeLabel(scopeFilter)} />
                </div>

                {dailyChart.length === 0 ? (
                    <EmptyState title="Nothing in this date range" detail="Widen the start and end dates to see measured volume." />
                ) : (
                    <div style={{ width: '100%', height: 420 }}>
                        <ResponsiveContainer>
                            <ComposedChart data={dailyChart} margin={{ top: 8, right: 16, bottom: 48, left: 8 }}>
                                <CartesianGrid stroke={GRID_COLOR} vertical={false} />
                                <XAxis
                                    dataKey="label"
                                    angle={-45}
                                    textAnchor="end"
                                    height={60}
                                    tick={{ fill: TEXT_SECONDARY, fontSize: 11 }}
                                    stroke={GRID_COLOR}
                                    interval="preserveStartEnd"
                                />
                                <YAxis
                                    tick={{ fill: TEXT_SECONDARY, fontSize: 11 }}
                                    stroke={GRID_COLOR}
                                    tickFormatter={(v) => v.toLocaleString()}
                                    label={{
                                        value: 'Items observed',
                                        angle: -90,
                                        position: 'insideLeft',
                                        style: { fill: TEXT_SECONDARY, fontSize: 11 }
                                    }}
                                />
                                <Tooltip content={<SeriesTooltip bucket={bucket} />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                                {chartedScopes.map(scope => (
                                    <Bar
                                        key={scope}
                                        dataKey={scope}
                                        stackId="volume"
                                        name={scopeLabel(scope)}
                                        fill={scopeColor(scope)}
                                        stroke="#fff"
                                        strokeWidth={1}
                                        radius={!showOther && scope === lastScope ? [3, 3, 0, 0] : 0}
                                    />
                                ))}
                                {showOther && (
                                    <Bar
                                        dataKey="__other__"
                                        stackId="volume"
                                        name={`Other (${overflowScopes.length} scopes)`}
                                        fill={OTHER_COLOR}
                                        stroke="#fff"
                                        strokeWidth={1}
                                        radius={[3, 3, 0, 0]}
                                    />
                                )}
                                {rollingPeriods > 0 && (
                                    <Line
                                        type="monotone"
                                        dataKey="rollingAvg"
                                        name={`${rollingPeriods}-period average`}
                                        stroke={TEXT_PRIMARY}
                                        strokeWidth={2}
                                        dot={false}
                                        activeDot={{ r: 4, stroke: '#fff', strokeWidth: 2 }}
                                    />
                                )}
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                )}

                <div style={{ marginTop: '12px' }}>
                    <Button size="small" onClick={() => setShowTable(v => !v)} sx={{ textTransform: 'none' }}>
                        {showTable ? 'Hide data table' : 'Show data table'}
                    </Button>
                </div>

                {showTable && (
                    <DataTable
                        caption={`${topicName} — volume by ${BUCKET_OPTIONS.find(b => b.value === bucket)?.label.toLowerCase()} bucket`}
                        columns={[
                            { key: 'label', label: 'Period' },
                            ...chartedScopes.map(s => ({
                                key: s,
                                label: scopeLabel(s),
                                align: 'right',
                                render: (r) => fmtInt(r[s])
                            })),
                            ...(overflowScopes.length > 0
                                ? [{ key: '__other__', label: 'Other', align: 'right', render: (r) => fmtInt(r.__other__) }]
                                : []),
                            { key: 'total', label: 'Total', align: 'right', render: (r) => fmtInt(r.total) }
                        ]}
                        rows={dailyChart.map(r => ({ ...r, key: r.bucketKey }))}
                    />
                )}
            </>
        );
    };

    const renderCommentsView = () => {
        if (commentRows.length === 0) {
            return (
                <EmptyState
                    title={`No comment tracking for ${topicName}`}
                    detail="Comment tracking needs scrape-window rows in topic_volume_stats with '<community>/comments' and '<community>/comment_mentions' scopes. None have been recorded for this product yet."
                />
            );
        }

        const volumeData = commentRows.map(r => ({
            key: r.key,
            community: r.community,
            scanned: r.scanned,
            mentions: r.mentions
        }));

        return (
            <>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
                    <StatTile label="Comments Scanned" value={fmtInt(commentTotals.scanned)} hint="full raw stream" />
                    <StatTile label="Mentions Kept" value={fmtInt(commentTotals.mentions)} hint="matched the topic" />
                    <StatTile label="Capture Rate" value={fmtPct(commentTotals.captureRate)} hint="kept ÷ scanned" />
                    <StatTile label="Communities" value={fmtInt(commentTotals.communities)} />
                </div>

                <div style={{
                    fontSize: '12px', color: TEXT_SECONDARY, lineHeight: 1.5,
                    marginBottom: '16px', maxWidth: 720
                }}>
                    Comment ingestion is gated — scanners walk the full stream but keep only
                    comments that mention the topic. These totals come from the scrape-window
                    measurement, which is the only record of how large the raw stream was.
                </div>

                {/* Only earns its space once there's more than one community to
                    compare — with a single one it's a lone bar pair restating the
                    tiles directly above it. */}
                {commentRows.length > 1 && (
                    <>
                        <div style={{
                            fontSize: '9px', fontWeight: 600, color: TEXT_MUTED,
                            textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px'
                        }}>
                            Raw stream vs. mentions kept, by community
                        </div>
                        <div style={{ width: '100%', height: Math.max(200, volumeData.length * 46 + 60) }}>
                            <ResponsiveContainer>
                                <BarChart
                                    data={volumeData}
                                    layout="vertical"
                                    margin={{ top: 4, right: 64, bottom: 8, left: 8 }}
                                    barGap={2}
                                >
                                    <CartesianGrid stroke={GRID_COLOR} horizontal={false} />
                                    <XAxis
                                        type="number"
                                        tick={{ fill: TEXT_SECONDARY, fontSize: 11 }}
                                        stroke={GRID_COLOR}
                                        tickFormatter={(v) => v.toLocaleString()}
                                    />
                                    <YAxis
                                        type="category"
                                        dataKey="community"
                                        width={130}
                                        tick={{ fill: TEXT_SECONDARY, fontSize: 11 }}
                                        stroke={GRID_COLOR}
                                    />
                                    <Tooltip content={<SeriesTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                                    <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                                    <Bar dataKey="scanned" name="Comments scanned" fill={SERIES_COLORS[0]} radius={[0, 3, 3, 0]}>
                                        <LabelList dataKey="scanned" position="right" formatter={fmtInt}
                                            style={{ fill: TEXT_SECONDARY, fontSize: 10 }} />
                                    </Bar>
                                    <Bar dataKey="mentions" name="Mentions kept" fill={SERIES_COLORS[1]} radius={[0, 3, 3, 0]}>
                                        <LabelList dataKey="mentions" position="right" formatter={fmtInt}
                                            style={{ fill: TEXT_SECONDARY, fontSize: 10 }} />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </>
                )}

                {commentDailyChart.length > 0 && (
                    <>
                        <div style={{
                            fontSize: '9px', fontWeight: 600, color: TEXT_MUTED,
                            textTransform: 'uppercase', letterSpacing: '0.5px',
                            marginTop: '28px', marginBottom: '8px'
                        }}>
                            Raw comment stream over time
                        </div>
                        <div style={{ width: '100%', height: 300 }}>
                            <ResponsiveContainer>
                                <ComposedChart data={commentDailyChart} margin={{ top: 8, right: 16, bottom: 48, left: 8 }}>
                                    <CartesianGrid stroke={GRID_COLOR} vertical={false} />
                                    <XAxis
                                        dataKey="label"
                                        angle={-45}
                                        textAnchor="end"
                                        height={60}
                                        tick={{ fill: TEXT_SECONDARY, fontSize: 11 }}
                                        stroke={GRID_COLOR}
                                        interval="preserveStartEnd"
                                    />
                                    <YAxis
                                        tick={{ fill: TEXT_SECONDARY, fontSize: 11 }}
                                        stroke={GRID_COLOR}
                                        tickFormatter={(v) => v.toLocaleString()}
                                    />
                                    <Tooltip content={<SeriesTooltip bucket={bucket} />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                                    <Bar dataKey="total" name="Comments scanned" fill={SERIES_COLORS[0]} radius={[3, 3, 0, 0]} />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>
                    </>
                )}

                <DataTable
                    caption="Comment tracking by community"
                    columns={[
                        { key: 'community', label: 'Community' },
                        { key: 'scanned', label: 'Scanned', align: 'right', render: (r) => fmtInt(r.scanned) },
                        { key: 'mentions', label: 'Mentions kept', align: 'right', render: (r) => fmtInt(r.mentions) },
                        { key: 'captureRate', label: 'Capture rate', align: 'right', render: (r) => fmtPct(r.captureRate) },
                        {
                            key: 'window',
                            label: 'Scrape window',
                            render: (r) => (r.windowStart && r.windowEnd
                                ? `${format(new Date(r.windowStart), 'MMM d, yyyy')} – ${format(new Date(r.windowEnd), 'MMM d, yyyy')}`
                                : '—')
                        }
                    ]}
                    rows={commentRows}
                />
            </>
        );
    };

    return (
        <div style={{ maxWidth: '100%', boxSizing: 'border-box' }}>
            <div
                onMouseEnter={() => setIsHeaderHovered(true)}
                onMouseLeave={() => setIsHeaderHovered(false)}
                style={{
                    backgroundColor: '#fafafa',
                    borderRadius: '8px',
                    padding: '20px 24px 18px',
                    marginBottom: '20px',
                    boxShadow: isHeaderHovered
                        ? '0 4px 12px rgba(0, 0, 0, 0.08)'
                        : '0 1px 3px rgba(0, 0, 0, 0.06)',
                    border: '1px solid rgba(0, 0, 0, 0.06)',
                    transition: 'box-shadow 0.2s ease',
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                }}
            >
                <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '11px', color: '#777', fontWeight: 500, lineHeight: 1.2, marginBottom: '1px' }}>
                        {companyName || 'Company'}
                    </div>
                    <h1 style={{ fontSize: '22px', fontWeight: 700, margin: 0, color: TEXT_PRIMARY, lineHeight: 1.25 }}>
                        {selectedProduct
                            ? `${topicName} · ${view === 'comments' ? 'Comment Tracking' : 'Post Volume'}`
                            : 'Select a Product'}
                    </h1>
                    {selectedProduct && (
                        <div style={{ fontSize: '12px', color: TEXT_MUTED, lineHeight: 1.3, marginTop: '2px' }}>
                            {subline}
                        </div>
                    )}
                </div>
                {renderControls()}
            </div>

            {renderBody()}
        </div>
    );
};

export default PostVolumePanel;
